import { Injectable } from '@angular/core';

import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { PresentationApiService } from 'src/app/api/services/presentation-api.service';
import { ScheduleApiService } from 'src/app/api/services/schedule-api.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { CompanyStateService } from './company-state.service';
import { UserStateService } from './user-state.service';
import {
  AccessLevel,
  AccessRequirement,
  DISPLAY_ACCESS,
  DisplayAccess,
  getAccessLevelRank,
  getEntityType,
  getFolderAccessLevel,
  meetsAccessLevel,
  PRESENTATION_ACCESS,
  PresentationAccess,
  SCHEDULE_ACCESS,
  ScheduleAccess,
  shareLabelToAccessLevel,
} from '../types/access-level';
import { ENTITY_TYPES, EntityType } from '../types/entity-type';

type EntityAccessLevelByType = {
  [ENTITY_TYPES.PRESENTATION]: PresentationAccess;
  [ENTITY_TYPES.SCHEDULE]: ScheduleAccess;
  [ENTITY_TYPES.DISPLAY]: DisplayAccess;
};

type RoleEntityAccessMap = {
  [K in EntityType]?: EntityAccessLevelByType[K];
};

type AccessControlList = Record<string, string>;

type SharedListItem = {
  id?: string;
  accessControlList?: AccessControlList | null;
};

const ENTITY_MINIMUM_ACCESS: Record<EntityType, AccessLevel> = {
  [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.EDITOR,
  [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.PLAYLIST_MANAGER,
  [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR,
};

const ROLE_ENTITY_ACCESS: Record<string, RoleEntityAccessMap> = {
  sa: {
    [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.FULL_ACCESS,
    [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.FULL_ACCESS,
    [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.FULL_ACCESS,
  },
  ua: {
    [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.FULL_ACCESS,
    [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.FULL_ACCESS,
    [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.FULL_ACCESS,
  },
  ce: {
    [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.EDITOR,
  },
  cp: {
    [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.PUBLISHER,
    [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.FULL_ACCESS,
  },
  da: {
    [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.FULL_ACCESS,
  },
  sm: {
    [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR,
  },
};

const ROLE_CODES = Object.keys(ROLE_ENTITY_ACCESS);
const SHARED_LIST_SEARCH = { sortBy: 'name', count: 1 };

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private shareCache = new Map<string, AccessLevel | null>();
  private pendingShares = new Map<string, Promise<AccessLevel | null>>();

  constructor(
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    private presentationApiService: PresentationApiService,
    private scheduleApiService: ScheduleApiService,
    private displayApiService: DisplayApiService,
    broadcasterService: BroadcasterService,
  ) {
    broadcasterService.on('risevision.company.selectedCompanyChanged', () => {
      this.shareCache.clear();
      this.pendingShares.clear();
    });
  }

  getRoleAccessLevel(
    entityType: typeof ENTITY_TYPES.PRESENTATION,
  ): PresentationAccess | null;
  getRoleAccessLevel(
    entityType: typeof ENTITY_TYPES.SCHEDULE,
  ): ScheduleAccess | null;
  getRoleAccessLevel(
    entityType: typeof ENTITY_TYPES.DISPLAY,
  ): DisplayAccess | null;
  getRoleAccessLevel(entityType: EntityType): AccessLevel | null {
    return this.resolveRoleAccessLevel(entityType);
  }

  areFoldersSupported(
    kind: EntityType | 'Presentation' | 'Schedule' | 'Display',
  ): boolean {
    return this.hasAccess(getFolderAccessLevel(kind));
  }

  isSaProductionReleaseEnabled(): boolean {
    return (
      this.userStateService.hasPermission('!PROD') ||
      this.userStateService.hasRole('sa')
    );
  }

  hasAccess(requiredLevel: AccessLevel, entityId?: string): boolean {
    const entityType = getEntityType(requiredLevel);

    if (entityType === null) {
      return false;
    }

    const roleLevel = this.resolveRoleAccessLevel(entityType);
    const shareLevel = entityId
      ? this.getCachedShareAccess(entityType, entityId)
      : null;
    const actualLevel = this.highestAccessLevel(roleLevel, shareLevel);

    if (actualLevel === null) {
      return false;
    }

    return meetsAccessLevel(actualLevel, requiredLevel);
  }

  meetsAccessRequirement(requirement: AccessRequirement = {}): boolean {
    const {
      requireAccess,
      requireRole,
      allowAssignedPublisher,
      allowSharedAccess,
      entityId,
    } = requirement;

    if (
      requireRole &&
      this.isEnvironmentOnlyRequirement(requireRole) &&
      !this.userStateService.hasPermission(requireRole)
    ) {
      return false;
    }

    if (this.userStateService.hasRole('sa')) {
      return true;
    }

    if (this.userStateService.hasRole('ua')) {
      return !this.isSaOnlyRequirement(requirement);
    }

    if (!requireAccess) {
      return this.userStateService.hasPermission(requireRole ?? '');
    }

    const accessGranted =
      this.hasAccess(requireAccess, entityId) ||
      (!!allowSharedAccess &&
        !entityId &&
        this.hasCachedSharedAccess(requireAccess)) ||
      (!!allowAssignedPublisher && this.userStateService.isAssignedPublisher());

    if (!requireRole) {
      return accessGranted;
    }

    const roleGranted = this.userStateService.hasPermission(requireRole);

    if (this.isEnvironmentOnlyRequirement(requireRole)) {
      return accessGranted && roleGranted;
    }

    return accessGranted || roleGranted;
  }

  async hasAnySharedAccess(requiredLevel: AccessLevel): Promise<boolean> {
    const entityType = getEntityType(requiredLevel);

    if (entityType === null) {
      return false;
    }

    const shareLevel = await this.loadFirstSharedAccess(entityType);

    return shareLevel !== null && meetsAccessLevel(shareLevel, requiredLevel);
  }

  async loadShareAccess(
    entityType: EntityType,
    entityId: string,
  ): Promise<AccessLevel | null> {
    return this.resolveAndCacheShare(entityType, entityId);
  }

  cacheListedShareAccess(
    entityType: EntityType,
    items: SharedListItem[],
  ): void {
    for (const item of items) {
      if (!item?.id) {
        continue;
      }

      void this.resolveAndCacheShare(
        entityType,
        item.id,
        item.accessControlList,
        false,
      );
    }
  }

  async loadFirstSharedAccess(
    entityType: EntityType,
  ): Promise<AccessLevel | null> {
    const companyId = this.companyStateService.getSelectedCompanyId();

    try {
      const item = await this.listFirstItem(entityType);

      if (!item?.id) {
        return null;
      }

      if (this.companyStateService.getSelectedCompanyId() !== companyId) {
        return this.shareLevelFromListedItem(entityType, item);
      }

      const shareLevel = await this.resolveAndCacheShare(
        entityType,
        item.id,
        item.accessControlList,
      );

      if (shareLevel) {
        return shareLevel;
      }

      const minimumAccess = ENTITY_MINIMUM_ACCESS[entityType];

      if (this.companyStateService.getSelectedCompanyId() !== companyId) {
        return minimumAccess;
      }

      this.shareCache.set(
        this.shareCacheKey(entityType, item.id),
        minimumAccess,
      );
      return minimumAccess;
    } catch {
      return null;
    }
  }

  hasCachedSharedAccess(requiredLevel: AccessLevel): boolean {
    const entityType = getEntityType(requiredLevel);

    if (entityType === null) {
      return false;
    }

    const prefix = `${this.companyStateService.getSelectedCompanyId()}:${entityType}:`;

    for (const [key, level] of this.shareCache) {
      if (!key.startsWith(prefix) || !level) {
        continue;
      }

      if (meetsAccessLevel(level, requiredLevel)) {
        return true;
      }
    }

    return false;
  }

  private isSaOnlyRequirement(requirement: AccessRequirement): boolean {
    const requireRole = requirement.requireRole?.trim();

    if (!requireRole) {
      return false;
    }

    const roleTokens = requireRole
      .split(/\s+/)
      .filter((token) => token && !token.startsWith('!'))
      .filter((token) => !this.userStateService.isEnvironmentRole(token));

    return roleTokens.length > 0 && roleTokens.every((role) => role === 'sa');
  }

  private isEnvironmentOnlyRequirement(requireRole: string): boolean {
    const roleTokens = requireRole
      .trim()
      .split(/\s+/)
      .filter((token) => token);

    return (
      roleTokens.length > 0 &&
      roleTokens.every((role) => this.userStateService.isEnvironmentRole(role))
    );
  }

  private resolveRoleAccessLevel(entityType: EntityType): AccessLevel | null {
    let highestRank = 0;
    let highestLevel: AccessLevel | null = null;

    for (const role of ROLE_CODES) {
      if (!this.userStateService.hasRole(role)) {
        continue;
      }

      const level = ROLE_ENTITY_ACCESS[role][entityType];
      if (!level) {
        continue;
      }

      const rank = getAccessLevelRank(level);
      if (rank !== null && rank > highestRank) {
        highestRank = rank;
        highestLevel = level;
      }
    }

    return highestLevel;
  }

  private getCachedShareAccess(
    entityType: EntityType,
    entityId: string,
  ): AccessLevel | null {
    return (
      this.shareCache.get(this.shareCacheKey(entityType, entityId)) ?? null
    );
  }

  private async resolveAndCacheShare(
    entityType: EntityType,
    entityId: string,
    accessControlList?: AccessControlList | null,
    fetchMissingAcl = true,
  ): Promise<AccessLevel | null> {
    const cacheKey = this.shareCacheKey(entityType, entityId);
    const listedLevel = accessControlList
      ? this.resolveShareAccessLevel(entityType, accessControlList)
      : null;

    if (listedLevel) {
      this.shareCache.set(cacheKey, listedLevel);
      return listedLevel;
    }

    if (this.shareCache.has(cacheKey)) {
      return this.shareCache.get(cacheKey) ?? null;
    }

    if (!fetchMissingAcl) {
      return null;
    }

    const pending = this.pendingShares.get(cacheKey);

    if (pending) {
      return pending;
    }

    const request = this.fetchAndCacheShare(entityType, entityId, cacheKey);
    this.pendingShares.set(cacheKey, request);

    try {
      return await request;
    } finally {
      this.pendingShares.delete(cacheKey);
    }
  }

  private async fetchAndCacheShare(
    entityType: EntityType,
    entityId: string,
    cacheKey: string,
  ): Promise<AccessLevel | null> {
    try {
      const acl = await this.getAccessControlList(entityType, entityId);
      const shareLevel = this.resolveShareAccessLevel(entityType, acl ?? {});
      this.shareCache.set(cacheKey, shareLevel);
      return shareLevel;
    } catch {
      this.shareCache.set(cacheKey, null);
      return null;
    }
  }

  private shareLevelFromListedItem(
    entityType: EntityType,
    item: SharedListItem,
  ): AccessLevel {
    const listedLevel = item.accessControlList
      ? this.resolveShareAccessLevel(entityType, item.accessControlList)
      : null;

    return listedLevel ?? ENTITY_MINIMUM_ACCESS[entityType];
  }

  private resolveShareAccessLevel(
    entityType: EntityType,
    accessControlList: AccessControlList,
  ): AccessLevel | null {
    const label = this.getShareLabel(accessControlList);

    if (!label) {
      return null;
    }

    return shareLabelToAccessLevel(label, entityType);
  }

  private getShareLabel(accessControlList: AccessControlList): string {
    const keys = this.currentUserAclKeys();

    for (const key of keys) {
      const label = accessControlList[key];
      if (label) {
        return label;
      }
    }

    const labelsByLowerKey = new Map<string, string>();
    for (const [aclKey, label] of Object.entries(accessControlList)) {
      if (label) {
        labelsByLowerKey.set(aclKey.toLowerCase(), label);
      }
    }

    for (const key of keys) {
      const label = labelsByLowerKey.get(key.toLowerCase());
      if (label) {
        return label;
      }
    }

    return '';
  }

  private currentUserAclKeys(): string[] {
    const profile = this.userStateService.getCopyOfProfile(true) ?? {};

    return [
      ...new Set(
        [
          this.userStateService.getUsername(),
          this.userStateService.getUserEmail(),
          profile.username,
          profile.id,
          profile.email,
        ].filter(
          (key): key is string => typeof key === 'string' && key.length > 0,
        ),
      ),
    ];
  }

  private highestAccessLevel(
    first: AccessLevel | null,
    second: AccessLevel | null,
  ): AccessLevel | null {
    if (first === null) {
      return second;
    }

    if (second === null) {
      return first;
    }

    const firstRank = getAccessLevelRank(first) ?? 0;
    const secondRank = getAccessLevelRank(second) ?? 0;

    return secondRank > firstRank ? second : first;
  }

  private shareCacheKey(entityType: EntityType, entityId: string): string {
    return `${this.companyStateService.getSelectedCompanyId()}:${entityType}:${entityId}`;
  }

  private async listFirstItem(
    entityType: EntityType,
  ): Promise<SharedListItem | undefined> {
    switch (entityType) {
      case ENTITY_TYPES.PRESENTATION: {
        const result =
          await this.presentationApiService.list(SHARED_LIST_SEARCH);
        return result.items?.[0];
      }
      case ENTITY_TYPES.SCHEDULE: {
        const result = await this.scheduleApiService.list(SHARED_LIST_SEARCH);
        return result.items?.[0];
      }
      case ENTITY_TYPES.DISPLAY: {
        const result = await this.displayApiService.list(SHARED_LIST_SEARCH);
        return result.items?.[0];
      }
      default:
        return undefined;
    }
  }

  private getAccessControlList(
    entityType: EntityType,
    entityId: string,
  ): Promise<AccessControlList> {
    switch (entityType) {
      case ENTITY_TYPES.PRESENTATION:
        return this.presentationApiService.getAccessControlList(entityId);
      case ENTITY_TYPES.SCHEDULE:
        return this.scheduleApiService.getAccessControlList(entityId);
      case ENTITY_TYPES.DISPLAY:
        return this.displayApiService.getAccessControlList(entityId);
      default:
        return Promise.resolve({});
    }
  }
}
